import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { WebhookEndpoint } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteWebhook } from '../hooks/use-webhook-mutations';

export interface DeleteEndpointDialogProps {
  storeId: string;
  endpoint: WebhookEndpoint | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

/** Removes the endpoint together with its delivery log. */
export function DeleteEndpointDialog({
  storeId,
  endpoint,
  onOpenChange,
  onDeleted,
}: DeleteEndpointDialogProps) {
  const { t } = useTranslation(['webhooks', 'common']);
  const remove = useDeleteWebhook(storeId);
  const message = useErrorMessage();

  return (
    <AlertDialog
      open={endpoint !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('webhooks:delete.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('webhooks:delete.description', { url: endpoint?.url ?? '' })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(remove.error)}</AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (endpoint) {
                remove.mutate(endpoint.id, {
                  onSuccess: () => {
                    onOpenChange(false);
                    onDeleted?.();
                  },
                });
              }
            }}
          >
            {remove.isPending ? t('webhooks:delete.submitting') : t('webhooks:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
